import * as Sentry from '@sentry/browser'
import { AttendaceShiftInsightFragment } from 'generated/resources/graphql'
import moment from 'moment'

import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { locationTypeText } from 'modules/attendance/lib/locationType'

type ShiftType = 'ClockedIn' | 'Break' | 'ClockedOut'
type DailyShift = 'beginning_of_day' | 'end_of_day' | null

const shifStatus = (lastShift: AttendaceShiftInsightFragment) => {
  return !lastShift?.workable ? 'Break' : lastShift?.clockOut ? 'ClockedOut' : 'ClockedIn'
}

const calculateOpenShift = (shift: AttendaceShiftInsightFragment) => {
  const openShiftClockinDate = stringToDate(shift.clockIn, 'backend_datetime')
  const openShiftClockinTime = moment(
    `${shift.date} ${openShiftClockinDate.utc().format('HH:mm:ss')}`,
    'YYYY/MM/D HH:mm:ss'
  )
  return moment.duration(moment().diff(openShiftClockinTime))
}

const calculateClosedShift = (shift: AttendaceShiftInsightFragment) => {
  const clockInMoment = moment(shift.clockIn)
  const clockOutMoment = moment(shift.clockOut)
  return moment.duration(clockOutMoment.diff(clockInMoment))
}

const filterShifts = (shifts: AttendaceShiftInsightFragment[]) => {
  const lastShift = shifts?.at(-1)
  if (!lastShift) return []

  if (lastShift.workable) {
    return shifts?.filter((shift) => shift.workable) || []
  } else {
    return [lastShift]
  }
}

export const MinutesFor = (shifts: AttendaceShiftInsightFragment[]) => {
  return (
    filterShifts(shifts).reduce((acc, shift) => {
      const duration = shift.clockOut ? calculateClosedShift(shift) : calculateOpenShift(shift)
      return acc + Math.floor(duration.asMinutes())
    }, 0) || 0
  )
}

export const getDailyShiftDisplayText = (dailyShift: DailyShift, hasCompletedDay: boolean) => {
  if (hasCompletedDay) {
    return i18n.t('clock_in.entire_day')
  }

  switch (dailyShift) {
    case 'beginning_of_day':
      return i18n.t('clock_in.first_half')
    case 'end_of_day':
      return i18n.t('clock_in.second_half')
  }
}

export const ShiftsData = (shifts: AttendaceShiftInsightFragment[]) => {
  const lastShift = shifts?.at(-1)

  const clockInDate = lastShift?.clockIn ? moment(lastShift.clockIn).utc().format('HH:mm') : null

  const clockInLocationType = lastShift?.locationType ?? undefined

  let clockInLocation: string | undefined
  if (lastShift?.workplace) {
    clockInLocation = lastShift.workplace.name
  }
  if (!clockInLocation && clockInLocationType) {
    clockInLocation = locationTypeText(clockInLocationType)
  }

  const shiftStatus: ShiftType | null = lastShift ? shifStatus(lastShift) : null
  const clockInProject =
    lastShift?.projectManagementTimeRecords?.at(0)?.projectWorker?.imputableProject?.name

  const dailyShift = lastShift?.halfDay
  const dailyShifts = shifts.filter((shift) => shift.halfDay != null)
  const hasCompletedDay = dailyShifts.length === 2

  return {
    clockInDate,
    clockInLocation,
    clockInLocationType,
    clockInProject,
    lastShift,
    shiftStatus,
    dailyShift,
    hasCompletedDay,
  }
}

export const formatClockInTime = (value: number) => {
  return value.toString().padStart(2, '0')
}

export const getShiftData = (status: ShiftType | null) => {
  switch (status) {
    case 'ClockedIn':
      return { text: i18n.t('insights.widgets.status.clocked_in'), value: 'positive' as const }
    case 'Break':
      return { text: i18n.t('insights.widgets.status.in_a_break'), value: 'warning' as const }
    case 'ClockedOut':
    case null:
      return { text: i18n.t('insights.widgets.status.clocked_out'), value: 'neutral' as const }
    default:
      Sentry.addBreadcrumb({
        message: 'Unknown shift status: ' + status,
        level: 'log',
      })
      const _exhaustiveCheck: never = status
      return _exhaustiveCheck
  }
}
